import type { JobPalletType, UserProfile } from './types';

export const PALLET_ASSIGN_USERNAME = 'pallet';

export const PALLET_TYPE_OPTIONS: { value: JobPalletType; label: string }[] = [
  { value: 'no_preset', label: 'No Preset' },
  { value: 'micro', label: 'Micro' },
  { value: 'half', label: 'Half' },
  { value: 'half_plus', label: 'Half +' },
  { value: 'full', label: 'Full' },
  { value: 'oversize_full', label: 'Oversize full' },
];

export type DeliveryAssignOption = { id: string; label: string; role: string };

export function isDeliveryOrCollectionRole(roleName: string): boolean {
  const r = roleName.toLowerCase();
  return r.includes('collection') || r.includes('delivery');
}

/** Build assign-delivery options; always include the `Pallet` user if present in profiles. */
export function buildDeliveryAssignOptions(
  userProfiles: { results?: UserProfile[] } | undefined
): DeliveryAssignOption[] {
  const list = userProfiles?.results || [];
  const filteredList = list.filter((p: UserProfile) =>
    isDeliveryOrCollectionRole(p.role_details?.name || '')
  );

  const options: DeliveryAssignOption[] = filteredList.map((p: UserProfile) => ({
    id: String(p.user),
    label: p.user_details.username,
    role: p.role_details?.name || 'No Role',
  }));

  const palletProfile = list.find(
    (p: UserProfile) => p.user_details.username.toLowerCase() === PALLET_ASSIGN_USERNAME
  );
  if (palletProfile && !options.some((o) => o.id === String(palletProfile.user))) {
    options.push({
      id: String(palletProfile.user),
      label: palletProfile.user_details.username,
      role: palletProfile.role_details?.name || 'No Role',
    });
  }

  return options;
}

export type DeliveryEngineerFilterOption = {
  id: string;
  username: string;
  full_name: string;
  role: string;
};

/** Engineer dropdown options for Delivery Management / calendar-style filters. */
export function buildDeliveryEngineerFilterOptions(
  userProfiles: { results?: UserProfile[] } | undefined
): DeliveryEngineerFilterOption[] {
  const list = userProfiles?.results || [];
  return buildDeliveryAssignOptions(userProfiles).map((opt) => {
    const profile = list.find((p) => String(p.user) === opt.id);
    const full_name = profile
      ? `${profile.user_details.first_name} ${profile.user_details.last_name}`.trim() ||
        opt.label
      : opt.label;
    return {
      id: opt.id,
      username: opt.label,
      full_name,
      role: opt.role,
    };
  });
}

export function isPalletAssignOptionLabel(username: string | undefined | null): boolean {
  return (username || '').trim().toLowerCase() === PALLET_ASSIGN_USERNAME;
}

/**
 * Selected user id is the Pallet placeholder user, or fallback to API username when profiles lack that row.
 */
export function isPalletDeliverySelection(
  selectedUserId: string,
  options: DeliveryAssignOption[],
  fallbackAssignedUsername: string | null | undefined
): boolean {
  if (selectedUserId) {
    const opt = options.find((o) => o.id === selectedUserId);
    if (opt) return isPalletAssignOptionLabel(opt.label);
  }
  return isPalletAssignOptionLabel(fallbackAssignedUsername);
}

/** Strip non-numeric input except one decimal point; user may paste "2 kg". */
export function parseGlassWeightKgInput(raw: string): number | null {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  const normalized =
    parts.length <= 1 ? cleaned : `${parts[0]}.${parts.slice(1).join('').replace(/\./g, '')}`;
  if (!normalized) return null;
  const n = parseFloat(normalized);
  return Number.isNaN(n) ? null : n;
}

export function formatGlassWeightKgForInput(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(n)) return '';
  return String(n);
}

export function formatGlassWeightKgPreviewKg(weight: number | null | undefined): string {
  if (weight === null || weight === undefined || Number.isNaN(weight)) return '—';
  const rounded = Number(Number(weight).toFixed(3));
  return `${rounded} kg`;
}

/** Compare API glass_weight_kg with raw input string for dirty-checking forms. */
/**
 * Ready to Book → Mark booked: allowed while still in production, or after production complete
 * (status completed + production_status production_complete).
 */
export function canMarkPalletBooked(job: {
  status: string;
  production_status?: string | null;
}): boolean {
  if (job.status === 'production') return true;
  return job.status === 'completed' && job.production_status === 'production_complete';
}

export function glassWeightKgMatchesJob(
  jobValue: string | number | null | undefined,
  inputRaw: string
): boolean {
  const fromJob = parseGlassWeightKgInput(formatGlassWeightKgForInput(jobValue));
  const fromInput = parseGlassWeightKgInput(inputRaw);
  if (fromJob === null && fromInput === null) return true;
  if (fromJob === null || fromInput === null) return false;
  return Math.abs(fromJob - fromInput) < 1e-6;
}
